// Light or dark, and the accent — TODO T30 (1 Sep 2026); light by default since T392a (18 Oct 2026);
// saved to the account since T570a (1 Nov 2026).
//
// The brain is LIGHT BY DEFAULT and green ("mona likes the white kind of look.. and i agree",
// Natanel, 18 Oct 2026); a recruiter may switch the theme to dark and the accent to blue or red.
// Both switches live in Settings and are the app's only user settings (design doc § 1.1).
//
// THERE IS NO `prefers-color-scheme` RULE, here or in the CSS. The default is the decision, not
// the operating system's (§ T30.3); reading the OS would make the product's own look a per-laptop
// accident, and a recruiter who wants dark says so once.
//
// THE ACCOUNT WINS (T570a, Natanel, 31 Oct: "Save it to your account"). A choice made in Settings
// is saved to the signed-in recruiter's account as well as this browser, and `AccountAppearance`
// polls the account every minute, so a change made in another browser, or asked of the brain's
// agent on WhatsApp, follows them. localStorage stays the first paint (`main.tsx` reads it before
// React mounts, and the sign-in screen has no account to read), and every account read is written
// back to it so the next reload paints the account's look at once. An account with nothing saved
// leaves the browser's own choice alone.
//
// Storage is per browser and guarded. `localStorage` throws outright in a few real
// configurations — a browser set to block site data, some private windows — and a brain that
// refuses to boot because it could not remember a colour would be a poor trade. Every access is
// wrapped, and every failure lands on the default. The keys are NEW at T392a: a "dark" saved
// under the old `vera-console-theme` is never read, so everyone meets the light look once.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { useMyAppearance, useSetMyAppearance } from '../api/me.ts';

export type Theme = 'dark' | 'light';
export type Accent = 'green' | 'blue' | 'red';

/** Exported for the harness boot alone, which pre-sets it from `?theme=` (T47). */
export const STORAGE_KEY = 'kadmon-brain-theme';
/** Exported for the harness boot alone, which pre-sets it from `?accent=` (T392a). */
export const ACCENT_KEY = 'kadmon-brain-accent';

export function readTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function readAccent(): Accent {
  try {
    const saved = localStorage.getItem(ACCENT_KEY);
    return saved === 'blue' || saved === 'red' ? saved : 'green';
  } catch {
    return 'green';
  }
}

/**
 * Puts the theme on `<html>`, where `tokens.css`'s `[data-theme='dark']` block can see it.
 *
 * Exported and called from `main.tsx` BEFORE the first render: setting it in an effect instead
 * would paint one light frame at every reload for a recruiter who chose dark, which reads as a
 * flicker rather than as a setting.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

/** The accent on `<html>`, where `tokens.css`'s `[data-accent]` blocks can see it. */
export function applyAccent(accent: Accent): void {
  document.documentElement.setAttribute('data-accent', accent);
}

/**
 * What an account read changes here: each of theme and accent the account holds and this browser
 * does not show. Null is nothing to change — the account has none, or it is already showing.
 */
export function fromAccount(
  account: { theme: Theme | null; accent: Accent | null },
  shown: { theme: Theme; accent: Accent },
): { theme?: Theme; accent?: Accent } | null {
  const change = {
    ...(account.theme && account.theme !== shown.theme ? { theme: account.theme } : {}),
    ...(account.accent && account.accent !== shown.accent ? { accent: account.accent } : {}),
  };
  return Object.keys(change).length > 0 ? change : null;
}

type Change = { theme?: Theme; accent?: Accent };

/**
 * T570a review round 1 (R5): a choice made here is kept until the account has it. A failed save
 * is sent again at the next account read, a choice made while one is being saved goes after it,
 * and no account read is adopted while a choice is unsaved — so neither a failed save nor a read
 * that left before the click can put the old look back. Pure, so the suite (Node, no React) runs it.
 */
export class AppearanceSync {
  private unsaved: Change | null = null;
  private sending: Promise<void> | null = null;
  private lastRead: string | null = null;

  private readonly send: (change: Change) => Promise<unknown>;

  constructor(send: (change: Change) => Promise<unknown>) {
    this.send = send;
  }

  /** A choice made here; resolves once it is saved, or has failed and waits for the next read. */
  choose(change: Change): Promise<void> {
    this.unsaved = { ...this.unsaved, ...change };
    return this.save();
  }

  /**
   * An account read: what to show here, or null. Only a CHANGE in what the account says is
   * adopted, and nothing while a choice made here is unsaved (which this read sends again).
   */
  read(
    account: { theme: Theme | null; accent: Accent | null },
    shown: { theme: Theme; accent: Accent },
  ): Change | null {
    const key = `${account.theme ?? ''}/${account.accent ?? ''}`;
    if (this.unsaved) {
      // Recorded while unsaved only when it is the choice being saved (fix rounds 2 and 3): then a
      // later change back elsewhere is a change, and a change made elsewhere is still adopted.
      if (fromAccount(account, { ...shown, ...this.unsaved }) === null) this.lastRead = key;
      void this.save();
      return null;
    }
    if (key === this.lastRead) return null;
    this.lastRead = key;
    return fromAccount(account, shown);
  }

  /** Sends what is unsaved, one save at a time; the one in flight when one is. */
  save(): Promise<void> {
    if (this.sending) return this.sending;
    if (!this.unsaved) return Promise.resolve();
    const sent = this.unsaved;
    this.sending = this.send(sent).then(
      () => {
        this.sending = null;
        const was = this.unsaved;
        const left: Change = {
          ...(was?.theme && was.theme !== sent.theme ? { theme: was.theme } : {}),
          ...(was?.accent && was.accent !== sent.accent ? { accent: was.accent } : {}),
        };
        this.unsaved = Object.keys(left).length > 0 ? left : null;
        return this.save();
      },
      () => {
        // Kept: the next account read sends it again.
        this.sending = null;
      },
    );
    return this.sending;
  }
}

interface ThemeContextValue {
  theme: Theme;
  /** A choice here: shown, remembered on this browser, and saved by `AccountAppearance`. */
  setTheme: (theme: Theme) => void;
  accent: Accent;
  setAccent: (accent: Accent) => void;
  /** The account's own, read back: shown and remembered here, never saved again. */
  adopt: (change: { theme?: Theme; accent?: Accent }) => void;
  /** The last choice made here not yet handed to `AccountAppearance`, which saves it. */
  unsaved: { theme?: Theme; accent?: Accent } | null;
  saved: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readTheme);
  const [accent, setAccentState] = useState<Accent>(readAccent);
  const [unsaved, setUnsaved] = useState<{ theme?: Theme; accent?: Accent } | null>(null);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  useEffect(() => {
    applyAccent(accent);
  }, [accent]);

  /**
   * The switch crossfades the whole page where the browser has view transitions (T47): the
   * attribute is set inside the transition's callback and the state flushed synchronously, so
   * the "new" snapshot is the new look rather than a frame of the old one. Without the API, or
   * under a reduced-motion preference, it is the plain switch it was. The choice is remembered
   * per browser; blocked storage holds it for this tab, which is the honest behaviour and better
   * than refusing to switch at all.
   */
  const change = useCallback((swap: () => void, key: string, value: string) => {
    const doc = document as Document & { startViewTransition?: (update: () => void) => unknown };
    if (
      typeof doc.startViewTransition === 'function' &&
      !matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      doc.startViewTransition(swap);
    } else {
      swap();
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage blocked: see above.
    }
  }, []);

  const showTheme = useCallback(
    (next: Theme) =>
      change(
        () => {
          applyTheme(next);
          flushSync(() => setThemeState(next));
        },
        STORAGE_KEY,
        next,
      ),
    [change],
  );

  const showAccent = useCallback(
    (next: Accent) =>
      change(
        () => {
          applyAccent(next);
          flushSync(() => setAccentState(next));
        },
        ACCENT_KEY,
        next,
      ),
    [change],
  );

  const setTheme = useCallback(
    (next: Theme) => {
      showTheme(next);
      setUnsaved((was) => ({ ...was, theme: next }));
    },
    [showTheme],
  );
  const setAccent = useCallback(
    (next: Accent) => {
      showAccent(next);
      setUnsaved((was) => ({ ...was, accent: next }));
    },
    [showAccent],
  );
  const adopt = useCallback(
    (next: { theme?: Theme; accent?: Accent }) => {
      if (next.theme) showTheme(next.theme);
      if (next.accent) showAccent(next.accent);
    },
    [showTheme, showAccent],
  );
  const saved = useCallback(() => setUnsaved(null), []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, accent, setAccent, adopt, unsaved, saved }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme outside ThemeProvider');
  return value;
}

/**
 * T570a: keeps this browser and the account level, mounted once the recruiter is signed in (the
 * shell). It hands a choice made here to `AppearanceSync`, which saves it, and shows what the
 * account holds when a poll brings something new — a change from another browser, or from the
 * brain's agent on WhatsApp.
 */
export function AccountAppearance(): null {
  const { theme, accent, adopt, unsaved, saved } = useTheme();
  const account = useMyAppearance();
  const save = useSetMyAppearance();
  const saveRef = useRef(save);
  saveRef.current = save;
  const sync = useRef<AppearanceSync | null>(null);
  sync.current ??= new AppearanceSync((change) => saveRef.current.mutateAsync(change));

  useEffect(() => {
    if (!unsaved) return;
    void sync.current?.choose(unsaved);
    saved();
  }, [unsaved, saved]);

  useEffect(() => {
    const data = account.data;
    if (!data) return;
    const change = sync.current?.read(data, { theme, accent });
    if (change) adopt(change);
    // Keyed on each poll's landing, never on what is shown: React Query keeps `data`'s reference
    // when the account is unchanged, and a failed save is sent again only by a read.
  }, [account.dataUpdatedAt, adopt]);

  return null;
}
